// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@stanion/App/Components/Common/XngZone";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import { constants } from "@stanion/XDL/constants";
import Filter from "@stanion/XDL/Icons/Filter";
import { color, hideElement, xScreen } from "@stanion/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return { isTabletScreen: state.xCommon.isTabletScreen };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;
export interface ProductListLayoutStyles {
    container?: InjectableCss;
    leftColumnContainer?: InjectableCss;
    rightColumnContainer?: InjectableCss;
    suggestionSectionContainer?: InjectableCss;
    productTitleSuggestionWrapper?: InjectableCss;
    actionsSectionContainer?: InjectableCss;
    sortFilterButton?: ButtonPresentationProps;
    categorySectionWrapper?: InjectableCss;
    actions?: InjectableCss;
    actionsRight?: InjectableCss;
    paginationBottom?: InjectableCss;
    viewItemWrapper?: InjectableCss;
    paginationList?: InjectableCss;
    countItemContainer?: InjectableCss;
    filterTabletScreen?: InjectableCss;
}

const sortAndFilterBreakpoint = constants.productList.xSortAndFilter.breakpoint;
export const layoutStyles: ProductListLayoutStyles = {
    container: {
        css: css`
            display: flex;
            gap: 54px;
        `,
    },
    leftColumnContainer: {
        css: css`
            flex: 0 0 285px;
            ${xScreen("lte", sortAndFilterBreakpoint)} {
                ${hideElement}
            }
        `,
    },
    rightColumnContainer: {
        css: css`
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    suggestionSectionContainer: {
        css: css`
            width: 100%;
            border-bottom: 1px solid ${color("common.border")};
            padding-bottom: 15px;
        `,
    },
    actionsSectionContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: no-wrap;
            padding-top: 16px;
            column-gap: 15px;
            ${xScreen("lte", "xs")} {
                column-gap: 0;
            }
        `,
    },
    sortFilterButton: {
        variant: "secondary",
        sizeVariant: "medium",
        icon: {
            src: Filter,
            position: "left",
        },
        css: css`
            ${xScreen("gt", sortAndFilterBreakpoint)} {
                ${hideElement}
            }
            min-height: 37px;
            padding: 10px 11px 10px 13px;
            margin-right: 20px;
            ${xScreen("lte", "xs")} {
                width: 160px;
                padding: 6px 7px 6px 9px;
            }

            ${IconWrapper} {
                margin-right: 12px;
                ${xScreen("lte", "xs")} {
                    margin-right: 8px;
                }
            }

            ${TypographyStyle} {
                ${xScreen("lte", "xs")} {
                    font-size: 10px;
                }
            }
        `,
    },
    categorySectionWrapper: {
        css: css`
            &:has(> div:empty) {
                display: none;
            }
        `,
    },
    actions: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            max-width: 369px;
            ${xScreen("lte", "xs")} {
                max-width: 100%;
            }
            justify-content: space-between;
        `,
    },
    actionsRight: {
        css: css`
            display: flex;
            align-items: center;
            column-gap: 15px;
        `,
    },
    paginationBottom: {
        css: css`
            margin: 0 auto;
            margin-top: 10px;
        `,
    },
    viewItemWrapper: {
        css: css`
            ${xScreen("lte", sortAndFilterBreakpoint)} {
                ${hideElement}
            }
        `,
    },
    paginationList: {
        css: css`
            ${xScreen("lte", "xs")} {
                ${hideElement}
            }
        `,
    },
    countItemContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
        `,
    },
    productTitleSuggestionWrapper: {
        css: css`
            width: 100%;
        `,
    },
    filterTabletScreen: {
        css: css`
            margin-top: 16px;
        `,
    },
};

const styles = layoutStyles;

const ProductListLayout: FC<Props> = ({ id, isTabletScreen }) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const modalOpenHandler = () => {
        setModalIsOpen(!modalIsOpen);
    };

    useEffect(() => {
        if (!isTabletScreen) {
            setModalIsOpen(false);
        }
    }, [isTabletScreen]);

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftColumnContainer}>
                <XngZone contentId={id} zoneName="ContentFilter" hint="Filter Section" />
            </StyledWrapper>
            <StyledWrapper {...styles.rightColumnContainer}>
                <StyledWrapper {...styles.categorySectionWrapper}>
                    <XngZone contentId={id} zoneName="ContentCategorySection" hint="Categories Section" />
                </StyledWrapper>
                <StyledWrapper {...styles.suggestionSectionContainer}>
                    <StyledWrapper
                        {...styles.productTitleSuggestionWrapper}
                        data-test-selector="productTitle-and-suggestion-productList"
                    >
                        <XngZone contentId={id} zoneName="ContentMainHeader06" hint="Suggestion" normalWidth={true} />
                        <StyledWrapper {...styles.countItemContainer}>
                            <Button onClick={modalOpenHandler} {...styles.sortFilterButton}>
                                {translate("Refine Results")}
                            </Button>
                            <XngZone
                                contentId={id}
                                zoneName="ContentMainHeader00"
                                hint="Count Item"
                                normalWidth={true}
                            />
                        </StyledWrapper>
                        {modalIsOpen && (
                            <StyledWrapper {...styles.filterTabletScreen}>
                                <XngZone contentId={id} zoneName="ContentFilter" hint="Filter Section" />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.actionsSectionContainer}>
                    <StyledWrapper {...styles.actions}>
                        <XngZone contentId={id} zoneName="ContentMainHeader01" normalWidth={true} hint="Sort Item" />
                        <XngZone
                            contentId={id}
                            zoneName="ContentMainHeader04"
                            normalWidth={true}
                            hint="Pagination PerPage"
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.actionsRight}>
                        <StyledWrapper {...styles.viewItemWrapper}>
                            <XngZone
                                contentId={id}
                                zoneName="ContentMainHeader03"
                                normalWidth={true}
                                hint="View Item"
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.paginationList}>
                            <XngZone
                                contentId={id}
                                zoneName="ContentMainHeader05"
                                normalWidth={true}
                                hint="Pagination Page"
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <XngZone contentId={id} zoneName="ContentMainSection" hint="Main Section" />
                <StyledWrapper {...styles.paginationBottom}>
                    <XngZone contentId={id} zoneName="ContentMainSection01" hint="Pagination" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(ProductListLayout),
    definition: {
        group: "Product List",
        displayName: "ProductList Layout",
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
